import { Exam, ImportType, ItemType, Note, Rating } from "@knowt/syncing/graphql/schema";
import dynamic from "next/dynamic";
import { memo } from "react";
import { DEFAULT_SIDEBAR_WIDTH, useSidebarContextSelector } from "../../../hooks/SidebarContext";
import SidebarBody from "../styled/SidebarBody";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import LazyLoaded from "@/components/LazyLoaded";
import { useNotePageContextSelector } from "@/components/NotePage/hooks/NotePageContext";
import { useRatingWhenExit } from "@/hooks/useRatingPopupWhenExit";
import { themeColors } from "@/utils/themeColors";
import NoteFile from "../NoteFile";
import ExamCard from "../ExamCard";
import StudyGuides from "../StudyGuides";
import { ChatButton, FlashcardsButton, MediaButton, PracticeTestButton } from "../SidebarButtons";
import AddRatingButton from "@/components/CircularButton/styled/AddRatingButton";
import StarRating from "@/components/StarRating";
import { spacing } from "@/utils/spacing";
import Explorer from "../NavigationPanel/Explorer";
import Mountable from "@/components/styled/div/Mountable";

const RatingPopup = dynamic(() => import("@/components/Popup/RatingPopup"));

const TOP_AP_EXAMS = ["AP English Language and Composition", "AP Biology", "AP United States History"];

const QuickInfoPanel = ({
    topNotes,
    examData,
    style,
    className,
}: {
    topNotes?: Note[];
    examData?: Exam;
    style?: React.CSSProperties;
    className?: string;
}) => {
    const noteId = useNotePageContextSelector(state => state.noteId);
    const note = useNotePageContextSelector(state => state.note);
    const isNoteOwner = useNotePageContextSelector(state => state.isNoteOwner);

    const isNoteSidebarCollapse = useSidebarContextSelector(state => state.isNoteSidebarCollapse);

    const ratingStats = {
        rating: note.rating,
        count: note.ratingCount,
    };

    const { onCloseRatingPopup, userRating, ratingPopupOpen, setRatingPopupOpen } = useRatingWhenExit({
        noteId,
        readOnly: !isNoteOwner,
    });

    return (
        <SidebarBody
            className={className}
            style={{
                height: "100vh",
                overflowY: "auto",
                width: !isNoteSidebarCollapse ? DEFAULT_SIDEBAR_WIDTH : 0,
                padding: !isNoteSidebarCollapse ? "2rem " : 0,
                backgroundColor: themeColors.neutralWhite,
                ...style,
            }}>
            <span className="bodyBold2" style={{ marginBottom: spacing.XS_2 }}>
                Note
            </span>

            {/* RATING */}
            <FlexRowAlignCenter style={{ justifyContent: "space-between", marginBottom: "1.2rem", gap: "0.5rem" }}>
                <span className="secondaryTextBold2">{ratingStats?.rating?.toFixed(1) || "0.0"}</span>
                <StarRating
                    readOnly
                    sx={{ fontSize: "1.8rem" }}
                    precision={0.5}
                    max={5}
                    value={ratingStats?.rating || 0}
                />
                <span className="secondaryText2">({ratingStats?.count || 0})</span>
                <AddRatingButton
                    setRatingPopupOpen={setRatingPopupOpen}
                    isRated={!!userRating}
                    sx={{ marginLeft: "auto" }}
                />
            </FlexRowAlignCenter>

            {/* TEST BUTTON */}
            <FlexColumn style={{ gap: spacing.XS_2, marginBottom: "2.4rem" }}>
                <PracticeTestButton />
                <ChatButton />
                <FlashcardsButton />
                <MediaButton />
            </FlexColumn>

            {/* SUGGESTED EXAM NOTES */}
            <Mountable mount={examData?.type === "AP" && !!examData?.units}>
                <StudyGuides examData={examData as Exam} />
                {/* TOP EXAMS */}
                <FlexColumn style={{ marginBottom: "pacin" }}>
                    <span className="secondaryTextBold1">{"Top Exams"}</span>
                    <FlexColumn style={{ gap: "1.5rem" }}>
                        {TOP_AP_EXAMS.map(examName => (
                            <ExamCard key={examName} name={examName} type="AP" />
                        ))}
                    </FlexColumn>
                </FlexColumn>
            </Mountable>

            <Mountable mount={!!note.classId}>
                <FlexColumn style={{ marginBottom: "2.4rem" }}>
                    <span className="secondaryTextBold1" style={{ marginBottom: "1.2rem" }}>
                        Class Notes
                    </span>
                    <Explorer classId={note.classId as string} />
                </FlexColumn>
            </Mountable>

            {/* SUGGESTED NOTES */}
            {topNotes?.length && !note.classId ? (
                <FlexColumn style={{ marginBottom: "2.4rem" }}>
                    <span className="secondaryTextBold1" style={{ marginBottom: "1.2rem" }}>
                        {!examData?.type ? "Explore Top Notes" : `Trending ${examData?.type} Study Guides`}
                    </span>
                    <FlexColumn style={{ gap: "1.5rem" }}>
                        {topNotes.slice(0, 6).map(n => (
                            <NoteFile key={n.noteId} note={n} />
                        ))}
                    </FlexColumn>
                </FlexColumn>
            ) : null}

            {/* TEXT PREVIEW */}
            {note?.importType === ImportType.PDF && (
                <FlexColumn style={{ marginBottom: "2.4rem" }}>
                    <span className="secondaryTextBold1" style={{ marginBottom: "1.2rem" }}>
                        Text Preview
                    </span>
                    <div style={{ fontSize: "1.45rem", maxHeight: "17rem", overflowY: "scroll" }}>
                        {/* SHOW TEXT CONTENT AND PARSED THE HTML TAG IF NECESSARY */}
                        {(note?.content || note?.summary)?.replace(/<\/?[^>]+>/gi, " ")}
                    </div>
                </FlexColumn>
            )}

            {/* POPUPS */}
            <LazyLoaded load={ratingPopupOpen}>
                <RatingPopup
                    documentId={noteId}
                    documentType={ItemType.NOTE}
                    documentOwnerId={note?.userId}
                    userRating={userRating as Rating | null}
                    isOpen={ratingPopupOpen}
                    onClose={onCloseRatingPopup}
                />
            </LazyLoaded>
        </SidebarBody>
    );
};

export default memo(QuickInfoPanel);
